import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Lock, Unlock, Check, Volume2, Sparkles, ChevronDown, ChevronUp, AlertTriangle, Map } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Objective = ({ o, color, i }) => (
  <motion.li initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }} data-testid={`week-objective-${o.key}`} className="space-y-1">
    <div className="flex items-center justify-between text-xs">
      <span className={`flex items-center gap-2 ${o.done ? 'text-zinc-300' : 'text-zinc-400'}`}>
        <span className={`w-4 h-4 rounded-full flex items-center justify-center border ${o.done ? 'bg-lime-400 border-lime-400' : 'border-zinc-700'}`}>{o.done && <Check className="w-3 h-3 text-black" />}</span>
        {o.label_pt}{!o.required && <span className="text-[9px] text-zinc-600">opcional</span>}
      </span>
      <span className="font-bold tabular-nums text-white">{o.current}<span className="text-zinc-500">/{o.target}</span></span>
    </div>
    <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden ml-6">
      <motion.div initial={{ width: 0 }} animate={{ width: `${o.pct}%` }} transition={{ duration: 0.8, ease: 'easeOut' }} className="h-full rounded-full" style={{ background: o.done ? '#a3e635' : color }} />
    </div>
  </motion.li>
);

export default function SpiritWeekGuideCard({ program, onWeekClosed, compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onMap = location.pathname.startsWith('/journey');
  const [g, setG] = useState(null);
  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState(null);
  const [open, setOpen] = useState(!compact);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/spirit/agent/week-guide/${program}`, auth()); setG(r.data); } catch (_) { setG({ enrolled: false }); }
  }, [program]);
  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const speak = async () => {
    const b = g?.briefing; if (!b) return;
    try {
      const r = await axios.post(`${API}/spirit/speak`, { text: `${b.message_pt} As tuas três ordens: ${b.orders_pt.join('. ')}.`, mood: 'wisdom', lang: 'pt' }, auth());
      if (r.data?.audio_base64) { audioRef.current?.pause(); audioRef.current = new Audio(`data:audio/${r.data.audio_format || 'mp3'};base64,${r.data.audio_base64}`); audioRef.current.play().catch(() => {}); }
    } catch (_) { /* silent */ }
  };

  const close = async () => {
    setClosing(true);
    try {
      const r = await axios.post(`${API}/spirit/agent/week-guide/${program}/close`, {}, auth());
      if (r.data.ok) {
        confetti({ particleCount: 180, spread: 100, origin: { y: 0.6 }, colors: ['#a3e635', '#22d3ee', '#fff', g?.phase?.color || '#84cc16'] });
        setClosed(r.data); onWeekClosed?.(r.data);
        if (r.data.phase_advanced && !onMap) { setTimeout(() => navigate(`/journey/${program}?portal=${r.data.next_phase_id}`), 1800); }
        else setTimeout(() => { setClosed(null); load(); }, 4200);
      }
    } finally { setClosing(false); }
  };

  if (!g) return <div className="h-24 rounded-2xl bg-zinc-900/40 border border-zinc-800 animate-pulse mb-6" data-testid="week-guide-loading" />;
  if (!g.enrolled) return null;
  const color = g.phase?.color || '#84cc16';
  const ready = g.status === 'ready_to_close';

  return (
    <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="spirit-week-guide" className="relative mb-6 rounded-2xl border bg-zinc-950/70 backdrop-blur-md overflow-hidden" style={{ borderColor: `${color}40`, boxShadow: `0 0 50px ${color}12` }}>
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
      <AnimatePresence>
        {closed && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="week-closed-overlay" className="absolute inset-0 z-10 bg-black/90 flex flex-col items-center justify-center text-center p-6">
            <motion.div initial={{ scale: 0.4 }} animate={{ scale: [0.4, 1.2, 1] }} transition={{ duration: 0.8 }} className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{ background: color, boxShadow: `0 0 80px ${color}` }}><Unlock className="w-9 h-9 text-black" /></motion.div>
            <p className="text-2xl font-black text-white">Semana {closed.week} fechada</p>
            <p className="text-sm text-zinc-400 mt-1">{closed.phase_advanced ? '🌀 Portal aberto — nova fase desbloqueada!' : closed.program_completed ? '🏆 Programa completo!' : `Semana ${closed.next_week} desbloqueada.`}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <button onClick={() => setOpen(v => !v)} className="w-full text-left p-5 flex items-start justify-between gap-3" data-testid="week-guide-toggle">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2"><Sparkles className="w-3 h-3" style={{ color }} /> O Spirit organizou a tua semana</p>
          <h3 className="text-lg sm:text-xl font-black text-white mt-1 truncate" data-testid="week-guide-title">
            {g.curriculum.emoji} Semana {g.week}<span className="text-zinc-600">/{g.total_weeks}</span> · {g.curriculum.title_pt}
          </h3>
          <p className="text-xs text-zinc-400 mt-0.5">{g.phase.name_pt} · dia {g.window.days_elapsed}/7 · <span style={{ color }} className="font-bold">{g.progress_pct}%</span></p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!onMap && <span role="link" data-testid="week-guide-map-link" onClick={e => { e.stopPropagation(); navigate(`/journey/${program}`); }} className="text-[10px] px-2 py-1 rounded-full border flex items-center gap-1 cursor-pointer hover:bg-white/5" style={{ borderColor: `${color}55`, color }}><Map className="w-3 h-3" /> Mapa</span>}
          {g.behind_schedule && <span data-testid="week-behind-badge" className="text-[10px] px-2 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> fechar</span>}
          {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-5 pb-5 space-y-5">
            {g.briefing && (
              <div className="rounded-xl p-4 border" style={{ background: `${color}0d`, borderColor: `${color}30` }} data-testid="week-briefing">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-zinc-200 leading-relaxed">{g.briefing.message_pt}</p>
                  <button onClick={speak} data-testid="week-briefing-speak" className="w-9 h-9 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-300 hover:text-white shrink-0" aria-label="Ouvir o Spirit"><Volume2 className="w-4 h-4" /></button>
                </div>
                <ol className="mt-3 space-y-1.5">
                  {g.briefing.orders_pt.map((o, i) => <li key={i} className="text-xs text-zinc-300 flex gap-2"><span className="font-black" style={{ color }}>{i + 1}.</span>{o}</li>)}
                </ol>
                {g.briefing.mantra_pt && <p className="mt-3 text-[11px] italic text-zinc-500">“{g.briefing.mantra_pt}”</p>}
              </div>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Objectivos para fechar a semana</p>
              <ul className="space-y-3">{g.objectives.map((o, i) => <Objective key={o.key} o={o} color={color} i={i} />)}</ul>
            </div>

            {g.curriculum.learn_pt && <p className="text-xs text-zinc-500"><span className="text-zinc-400 font-semibold">Aprender esta semana:</span> {g.curriculum.learn_pt}</p>}

            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="next-week-lock">
                {g.next_week ? (<><Lock className="w-3.5 h-3.5" /> Semana {g.next_week} bloqueada até fechares esta</>) : (<><Sparkles className="w-3.5 h-3.5" /> Última semana do programa</>)}
              </div>
              <motion.button whileTap={{ scale: 0.96 }} disabled={!ready || closing} onClick={close} data-testid="close-week-btn"
                className="px-5 py-2.5 rounded-full text-sm font-black text-black disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: ready ? `linear-gradient(135deg, ${color}, #a3e635)` : '#3f3f46', boxShadow: ready ? `0 0 30px ${color}66` : 'none' }}>
                {closing ? 'A fechar…' : ready ? '🏁 Fechar semana' : `Faltam ${g.objectives.filter(o => o.required && !o.done).length}`}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
